import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./UnlockBenefits.css";

/* ── Tier definitions ── */
const TIERS = [
  { id: "silver",   label: "Silver",   equity: 5000,   flexSlots: 2, color: "#9aa5be", sub: false },
  { id: "gold",     label: "Gold",     equity: 10000,  flexSlots: 3, color: "#f5c542", sub: false },
  { id: "platinum", label: "Platinum", equity: 25000,  flexSlots: 4, color: "#a5d8ff", sub: false },
  { id: "diamond",  label: "Diamond",  equity: 250000, flexSlots: 5, color: "#6fdc8c", sub: false },
];

/* ── Benefit packs and which tier unlocks them ── */
const PACKS = [
  { id: "lounges",    icon: "✈️", name: "Airport Lounge Pack",   desc: "Unlimited lounge + Fast Track",          unlockedAt: "silver"   },
  { id: "insurance",  icon: "🛡️", name: "Insurance Super Pack",   desc: "Purchase + warranty + phone protection", unlockedAt: "gold"     },
  { id: "hotels",     icon: "🏨", name: "Hotel VIP Pack",         desc: "Luxury Hotels + Relais & Châteaux",      unlockedAt: "gold"     },
  { id: "travel",     icon: "✈️", name: "Travel Super Pack",      desc: "Trip insurance + medical + car rental",  unlockedAt: "platinum" },
  { id: "golf",       icon: "🏌️", name: "Golf & Wellness",        desc: "Troon Rewards + spa access",             unlockedAt: "platinum" },
  { id: "tech",       icon: "📱", name: "Tech Protection",        desc: "Phone + gadget insurance + eSIM",        unlockedAt: "platinum" },
  { id: "invest",     icon: "💰", name: "Investment Boost",       desc: "Extra 1% stock-back rewards",            unlockedAt: "diamond"  },
  { id: "entertain",  icon: "🎭", name: "Entertainment",          desc: "20% off venues + StubHub + events",      unlockedAt: "diamond"  },
];

/* Demo: user is currently at Gold */
const USER_TIER = "gold";
const USER_EQUITY = 8500;

const TIER_ORDER = ["silver", "gold", "platinum", "diamond"];
const tierIndex = (id) => TIER_ORDER.indexOf(id);
const isUnlocked = (pack, userTier) => tierIndex(pack.unlockedAt) <= tierIndex(userTier);

const SUBSCRIPTION_PLANS = [
  { id: "essential", label: "Essential", price: "Free",     slots: 2, perks: ["Airport Lounge Pack", "Basic Insurance"] },
  { id: "pro",       label: "Pro",       price: "$14.99/mo", slots: 4, perks: ["All Platinum perks", "Hotel VIP Pack", "Travel Super Pack", "Golf & Wellness"] },
  { id: "ultra",     label: "Ultra",     price: "$29.99/mo", slots: 5, perks: ["All Diamond perks", "Investment Boost", "Entertainment", "Concierge 24/7"] },
];

export default function UnlockBenefits() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("equity"); // equity | subscription
  const [selectedPlan, setSelectedPlan] = useState("pro");

  const userTierDef = TIERS.find(t => t.id === USER_TIER);
  const nextTierIndex = tierIndex(USER_TIER) + 1;
  const nextTier = TIERS[nextTierIndex];
  const equityProgress = Math.min((USER_EQUITY / (nextTier?.equity || USER_EQUITY)) * 100, 100);
  const depositNeeded = nextTier ? nextTier.equity - USER_EQUITY : 0;

  const unlockedPacks = PACKS.filter(p => isUnlocked(p, USER_TIER));
  const lockedPacks = PACKS.filter(p => !isUnlocked(p, USER_TIER));

  return (
    <div className="ub-page">
      {/* Header */}
      <div className="ub-header">
        <button className="ub-back" type="button" onClick={() => navigate(-1)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0f2f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <span className="ub-title">Unlock Benefits</span>
        <span style={{ width: 30 }} />
      </div>

      <div className="ub-scroll">
        {/* Current status */}
        <div className="ub-status-card">
          <div className="ub-status-top">
            <div>
              <span className="ub-status-label">Current Tier</span>
              <span className="ub-status-tier" style={{ color: userTierDef.color }}>◆ {userTierDef.label}</span>
            </div>
            <div className="ub-status-equity">
              <span className="ub-equity-value">${USER_EQUITY.toLocaleString()}</span>
              <span className="ub-equity-label">Portfolio equity</span>
            </div>
          </div>
          <div className="ub-status-unlocked">
            <span className="ub-unlocked-count">{unlockedPacks.length}</span>
            <span className="ub-unlocked-label"> of {PACKS.length} benefit packs unlocked</span>
          </div>
        </div>

        {/* Locked packs preview */}
        {lockedPacks.length > 0 && (
          <div className="ub-locked-preview">
            <span className="ub-section-label">🔒 Locked — upgrade to access</span>
            {lockedPacks.map(p => {
              const unlockTier = TIERS.find(t => t.id === p.unlockedAt);
              return (
                <div className="ub-locked-row" key={p.id}>
                  <div className="ub-locked-icon">{p.icon}</div>
                  <div className="ub-locked-info">
                    <span className="ub-locked-name">{p.name}</span>
                    <span className="ub-locked-desc">{p.desc}</span>
                  </div>
                  <span className="ub-locked-tier" style={{ color: unlockTier.color }}>
                    {unlockTier.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Unlock paths tabs */}
        <div className="ub-tabs">
          <button
            className={`ub-tab ${activeTab === "equity" ? "active" : ""}`}
            type="button"
            onClick={() => setActiveTab("equity")}
          >
            📈 Boost Equity
          </button>
          <button
            className={`ub-tab ${activeTab === "subscription" ? "active" : ""}`}
            type="button"
            onClick={() => setActiveTab("subscription")}
          >
            ⭐ Subscription
          </button>
        </div>

        {/* ── Equity path ── */}
        {activeTab === "equity" && nextTier && (
          <div className="ub-path-card">
            <div className="ub-path-header">
              <span className="ub-path-title">Reach {nextTier.label}</span>
              <span className="ub-path-sub">+{nextTier.flexSlots - userTierDef.flexSlots} more benefit packs unlocked</span>
            </div>

            {/* Progress bar */}
            <div className="ub-equity-progress">
              <div className="ub-eq-labels">
                <span style={{ color: userTierDef.color }}>◆ {userTierDef.label} — ${USER_EQUITY.toLocaleString()}</span>
                <span style={{ color: nextTier.color }}>◆ {nextTier.label} — ${nextTier.equity.toLocaleString()}</span>
              </div>
              <div className="ub-eq-bar-bg">
                <div className="ub-eq-bar-fill" style={{ width: `${equityProgress}%`, background: nextTier.color }} />
              </div>
              <span className="ub-eq-need">Deposit <strong>${depositNeeded.toLocaleString()}</strong> more to unlock</span>
            </div>

            {/* Packs unlocking at next tier */}
            <div className="ub-unlocks-list">
              <span className="ub-unlocks-label">Unlocks at {nextTier.label}:</span>
              {PACKS.filter(p => p.unlockedAt === nextTier.id).map(p => (
                <div className="ub-unlock-row" key={p.id}>
                  <span className="ub-unlock-icon">{p.icon}</span>
                  <span className="ub-unlock-name">{p.name}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#13c636" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                </div>
              ))}
            </div>

            <button className="ub-path-cta" type="button">Deposit Now</button>

            {/* All tiers ladder */}
            <div className="ub-tiers-ladder">
              {TIERS.map((t, i) => {
                const reached = tierIndex(t.id) <= tierIndex(USER_TIER);
                return (
                  <div key={t.id} className={`ub-ladder-row ${reached ? "reached" : ""}`}>
                    <div className="ub-ladder-dot" style={{ background: reached ? t.color : "rgba(255,255,255,0.1)", borderColor: t.color }} />
                    <span className="ub-ladder-label" style={{ color: reached ? t.color : "#7a8299" }}>◆ {t.label}</span>
                    <span className="ub-ladder-equity">${t.equity >= 1000 ? `${t.equity/1000}K` : t.equity}</span>
                    <span className="ub-ladder-slots">{t.flexSlots} packs</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Subscription path ── */}
        {activeTab === "subscription" && (
          <div className="ub-sub-cards">
            {SUBSCRIPTION_PLANS.map(plan => (
              <button
                key={plan.id}
                type="button"
                className={`ub-sub-card ${selectedPlan === plan.id ? "selected" : ""}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <div className="ub-sub-top">
                  <div>
                    <span className="ub-sub-name">{plan.label}</span>
                    <span className="ub-sub-slots">{plan.slots} benefit packs</span>
                  </div>
                  <div className="ub-sub-price-wrap">
                    <span className="ub-sub-price">{plan.price}</span>
                    {selectedPlan === plan.id && (
                      <div className="ub-sub-check">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <div className="ub-sub-perks">
                  {plan.perks.map((perk, i) => (
                    <span key={i} className="ub-sub-perk">✓ {perk}</span>
                  ))}
                </div>
              </button>
            ))}

            <button className="ub-path-cta" type="button">
              Upgrade to {SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.label}
            </button>
          </div>
        )}

        {/* Active benefits */}
        <div className="ub-active-section">
          <span className="ub-section-label">✅ Your active benefit packs</span>
          {unlockedPacks.map(p => (
            <div className="ub-active-row" key={p.id}>
              <div className="ub-active-icon">{p.icon}</div>
              <div className="ub-active-info">
                <span className="ub-active-name">{p.name}</span>
                <span className="ub-active-desc">{p.desc}</span>
              </div>
              <div className="ub-active-check">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#13c636" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            </div>
          ))}
        </div>

        <div style={{ height: 120 }} />
      </div>
    </div>
  );
}
